/**
 * WalletPage — Main wallet view
 *
 * PRD-03: Wallet System (Balances & Transfers)
 *
 * CRITICAL FIX (2025-12-11):
 * - Use direct imports (PageHeader, PageContent) instead of Page.Header/Page.Content
 * - Page.Header/Page.Content become undefined in production due to tree-shaking
 * - See: packages/ui/src/components/page.tsx for named exports
 */

import { WalletPageSkeleton } from "@/components/skeletons/WalletPageSkeletons";
import {
  BalanceCard,
  P2PTransferSheet,
  TransactionHistory,
  VirtualAccountsSection,
  useWalletBalances,
  useWalletRealtime,
  useWalletTransactions
} from "@/features/user/wallet";
import { toast } from '@/hooks/use-toast';
import { useProfileData } from "@/hooks/useProfileData";
import { trackUX, UX_EVENTS } from '@/lib/analytics/ux-events';
import { sfIcons } from "@/lib/sf-icons";
import { supabaseBrowser } from '@/lib/supabase-browser';
import { semanticTokens } from '@packages/config/theme';
// CRITICAL: Import PageHeader and PageContent directly - not via Page.Header
import { Page, PageHeader, PageContent } from "@supabank/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AddFundsWizard } from './AddFundsWizard';
import { WalletCreationModal } from './WalletCreationModal';
import { WalletOnboardingState } from './WalletOnboardingState';
import { WithdrawWizard } from './WithdrawWizard';

export default function WalletPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // Enable Realtime updates
  useWalletRealtime();

  // Get profile data for KYC check
  const { kycStatus, loading: profileLoading } = useProfileData();

  // Get wallet data
  const { data: wallets, isLoading: walletsLoading, refetch: refetchWallets } = useWalletBalances();
  const { data: transactions } = useWalletTransactions();

  const [isTransferOpen, setTransferOpen] = useState(false);
  const [isAddFundsOpen, setAddFundsOpen] = useState(false);
  const [isWithdrawOpen, setWithdrawOpen] = useState(false);
  const [isCreatingWallet, setIsCreatingWallet] = useState(false);
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);

  // Check first_time from URL
  const isFirstTime = searchParams.get('first_time') === 'true';

  // Compute states
  const hasTransactions = useMemo(() => transactions && transactions.length > 0, [transactions]);

  // KYC check: canCreateAddress means KYC approved
  const canCreateAddress = kycStatus === 'approved';

  // PRD-USER-ONBOARDING-MASTER: Auto-provisioning
  const autoProvisionAttempted = useRef(false);
  const [isAutoProvisioning, setIsAutoProvisioning] = useState(false);

  // PRD-USER-ONBOARDING-MASTER: Auto-provision wallet ALWAYS when KYC approved + no wallets
  useEffect(() => {
    const shouldAutoProvision =
      canCreateAddress &&
      !walletsLoading &&
      wallets !== undefined &&
      wallets.length === 0 &&
      !autoProvisionAttempted.current &&
      !isCreatingWallet &&
      !isAutoProvisioning;

    if (shouldAutoProvision) {
      autoProvisionAttempted.current = true;
      setIsAutoProvisioning(true);

      (async () => {
        try {
          const { data, error } = await supabaseBrowser.functions.invoke('wallet', {
            body: { action: 'user.create', chain: 'base' }
          });

          if (data?.error || data?.code) {
            throw new Error(data?.error || data?.message || 'Failed to create wallet');
          }

          if (error) throw error;

          await queryClient.invalidateQueries({ queryKey: ['wallet-balances'] });
          await refetchWallets();

          trackUX(UX_EVENTS.WALLET_CREATED, {
            chain: 'base',
            asset: 'usdc',
            source: 'onboarding',
          });

          toast.success('Your wallet is ready!');
          navigate('/dashboard/wallet', { replace: true });
        } catch (err) {
          console.error('[WalletPage] Auto-provision failed:', err);
        } finally {
          setIsAutoProvisioning(false);
        }
      })();
    }
  }, [canCreateAddress, walletsLoading, wallets, isCreatingWallet, isAutoProvisioning, queryClient, refetchWallets, navigate]);

  // Show skeleton until ALL critical data is ready
  const isLoading = profileLoading || (walletsLoading && wallets === undefined) || isAutoProvisioning;

  // Handle wallet creation with chain and asset selection
  const handleCreateWallet = async (chain: string, asset: string) => {
    if (!canCreateAddress) {
      toast.error('Please complete verification first');
      return;
    }

    setIsCreatingWallet(true);
    setIsWalletModalOpen(false);

    try {
      const { data, error } = await supabaseBrowser.functions.invoke('wallet', {
        body: { action: 'user.create', chain }
      });

      if (data?.error || data?.code) {
        throw new Error(data?.error || data?.message || 'Failed to create wallet');
      }

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['wallet-balances'] });
      await refetchWallets();

      trackUX(UX_EVENTS.WALLET_CREATED, {
        chain,
        asset,
        source: isFirstTime ? 'onboarding' : 'manual',
      });

      if (isFirstTime) {
        navigate('/dashboard/wallet', { replace: true });
      }

      toast.success('Wallet created successfully!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create wallet');
      setIsWalletModalOpen(true);
    } finally {
      setIsCreatingWallet(false);
    }
  };

  // Get wallet icon (with fallback)
  const WalletIcon = sfIcons?.wallet ?? (() => null);
  const spacingClass = semanticTokens?.spacing?.lg ?? 'p-6';

  // Loading state - show skeleton
  if (isLoading) {
    return (
      <Page data-ux-contract="canon" className={spacingClass}>
        <PageHeader
          icon={WalletIcon}
          title="Wallet"
        />
        <PageContent>
          <WalletPageSkeleton />
        </PageContent>
      </Page>
    );
  }

  // KYC not approved - show verification blocker
  if (!canCreateAddress) {
    return (
      <Page data-ux-contract="canon" className={spacingClass}>
        <PageHeader
          icon={WalletIcon}
          title="Wallet"
        />
        <PageContent>
          <WalletOnboardingState
            kycStatus={kycStatus}
            canCreateAddress={canCreateAddress}
          />
        </PageContent>
      </Page>
    );
  }

  // Main wallet view - PRD-03 structure: Header → Balances → History
  return (
    <Page data-ux-contract="canon" className={spacingClass}>
      <PageHeader
        icon={WalletIcon}
        title="Wallet"
      />

      <PageContent>
        {/* Balances Section - PRD-03 §5: BalanceCard with actions */}
        <BalanceCard
          showActions={true}
          onAddFunds={() => setAddFundsOpen(true)}
          onWithdraw={() => setWithdrawOpen(true)}
          onTransfer={() => setTransferOpen(true)}
        />

        {/* Virtual Accounts Section - PRD-USER-VA: Bank details for receiving fiat */}
        <VirtualAccountsSection
          onCreateVA={() => setAddFundsOpen(true)}
        />

        {/* Transaction History - PRD-03 §5: Recent activity */}
        <TransactionHistory showExportButton={hasTransactions} />
      </PageContent>

      {/* Modals/Wizards - PRD-03 §5: Transfer Flow */}
      <P2PTransferSheet
        open={isTransferOpen}
        onClose={() => setTransferOpen(false)}
        onSuccess={() => refetchWallets()}
      />

      <AddFundsWizard
        open={isAddFundsOpen}
        onClose={() => setAddFundsOpen(false)}
        onSuccess={() => refetchWallets()}
      />

      <WithdrawWizard
        open={isWithdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        onSuccess={() => refetchWallets()}
      />

      <WalletCreationModal
        open={isWalletModalOpen}
        onClose={() => setIsWalletModalOpen(false)}
        onSuccess={handleCreateWallet}
        countryCode={null}
        isCreating={isCreatingWallet}
      />
    </Page>
  );
}
